import { useEffect, useRef, useState } from "react";

/*
 * Speak in "Send feedback": records from the microphone; the recording goes out with the feedback like a
 * screenshot and the server types it out (apps/server/src/transcribe.ts). Chat 16 waited here for the words; the
 * owner (16 Oct): "takes way too long… when I hit submit, I want it to look like it's done", so nothing here
 * waits on the network. Works the same in Chrome and in the Android app (voice notes already record there).
 */

const TYPES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];
/** Recordings stop here (~720 KB at this bit rate). */
export const VOICE_MAX_MS = 3 * 60_000;
const BITS = 32_000; // plenty for speech, and half the upload of the voice notes' 64 kbps

export type VoicePhase = "idle" | "starting" | "recording";
export type Recording = { blob: Blob; ms: number };

function pickType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

type Session = {
  rec: MediaRecorder | null;
  stream: MediaStream | null;
  ctx: AudioContext | null;
  timer: number;
  t0: number;
  keep: boolean;
  /** Settles when the session ends: the recording, or null (cancelled, nothing recorded, no microphone). */
  done: Promise<Recording | null>;
  finish: (r: Recording | null) => void;
};

/** Microphone → a recording. `onRecorded` gets it when it stops (Stop, the 3-minute limit, or Send). */
export function useVoiceRecorder(onRecorded: (r: Recording) => void) {
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const session = useRef<Session | null>(null);
  const cb = useRef(onRecorded);
  cb.current = onRecorded;
  const alive = useRef(true);

  const cleanup = (s: Session) => {
    clearInterval(s.timer);
    void s.ctx?.close().catch(() => undefined);
    s.stream?.getTracks().forEach((t) => t.stop());
    if (session.current === s) session.current = null;
  };

  const start = async () => {
    if (session.current) return;
    setError(null);
    setElapsed(0);
    setLevel(0);
    setPhase("starting");
    let finish: (r: Recording | null) => void = () => undefined;
    const done = new Promise<Recording | null>((r) => (finish = r));
    const s: Session = { rec: null, stream: null, ctx: null, timer: 0, t0: 0, keep: false, done, finish };
    session.current = s;
    const cancelled = () => session.current !== s;
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("This browser can't record audio.");
      s.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      if (cancelled()) return void s.stream.getTracks().forEach((t) => t.stop());
      const type = pickType();
      const rec = new MediaRecorder(s.stream, type ? { mimeType: type, audioBitsPerSecond: BITS } : undefined);
      s.rec = rec;
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        const ms = performance.now() - s.t0;
        cleanup(s);
        const r = s.keep && chunks.length ? { blob: new Blob(chunks, { type: (rec.mimeType || type || "audio/webm").split(";")[0] }), ms } : null;
        s.finish(r);
        if (!alive.current) return;
        setPhase("idle");
        if (r) cb.current(r);
      };
      rec.start(1000);
      s.t0 = performance.now();
      setPhase("recording");
      // A level meter, so it's clear the microphone hears you; a timer, not rAF (keeps counting in the background).
      s.ctx = new AudioContext();
      const an = s.ctx.createAnalyser();
      an.fftSize = 512;
      s.ctx.createMediaStreamSource(s.stream).connect(an);
      const buf = new Uint8Array(an.fftSize);
      s.timer = window.setInterval(() => {
        an.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
        setLevel(peak / 128);
        const ms = performance.now() - s.t0;
        setElapsed(ms);
        if (ms >= VOICE_MAX_MS) void stop();
      }, 100);
    } catch (e) {
      cleanup(s);
      s.finish(null);
      if (!alive.current) return;
      setPhase("idle");
      setError(
        e instanceof DOMException && e.name === "NotAllowedError"
          ? "Microphone access is off for Engram. Allow it in the browser's site settings (in the app: Android Settings > Apps > Engram > Permissions)."
          : e instanceof Error
            ? e.message
            : "Can't record audio.",
      );
    }
  };

  /** Stops and keeps the recording; resolves with it (null if nothing was recorded). */
  const stop = (): Promise<Recording | null> => {
    const s = session.current;
    if (!s) return Promise.resolve(null);
    s.keep = true;
    if (s.rec && s.rec.state !== "inactive") s.rec.stop();
    else cancel(); // still starting: nothing recorded yet
    return s.done;
  };

  /** Throws the recording away. */
  const cancel = () => {
    const s = session.current;
    if (!s) return;
    s.keep = false;
    if (s.rec && s.rec.state !== "inactive") s.rec.stop();
    else {
      cleanup(s);
      s.finish(null);
      setPhase("idle");
    }
  };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      const s = session.current;
      if (!s) return;
      s.keep = false;
      if (s.rec && s.rec.state !== "inactive") s.rec.stop();
      cleanup(s);
      s.finish(null);
    };
  }, []);

  return { phase, elapsed, level, error, start, stop, cancel };
}
